import React from 'react';

const formatSegmentTime = (seconds) => {
  const s = Number(seconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${sec.toFixed(3)}`;
};

/** Returns true if two segments overlap in time. */
function segmentsOverlap(a, b) {
  return a.start < b.end && a.end > b.start;
}

/** For each segment in segmentsA, true if no segment in segmentsB overlaps it. Exported for use in comparison. */
export function computeSegmentMissingInOther(segmentsA, segmentsB) {
  if (!Array.isArray(segmentsA) || segmentsA.length === 0) return [];
  if (!Array.isArray(segmentsB) || segmentsB.length === 0) return segmentsA.map(() => true);
  return segmentsA.map((segA) => !segmentsB.some((segB) => segmentsOverlap(segA, segB)));
}

/** Read-only transcription box - same card style as TranscriptionCard. Shows timestamped segments when transcript_segments is present. Optional segmentMissingInOther (bool[]) and missingLabel to show diff. Optional videoCurrentTime (number) and onSegmentSeek (fn) for video sync. When embedded=true, no card/header (for use inside page section). */
const TranscriptionReadOnlyBox = ({ transcript, title = 'Transcription', segmentMissingInOther = null, missingLabel = '', videoCurrentTime, onSegmentSeek, embedded = false }) => {
  const text = transcript?.transcript_text || transcript?.text || '';
  const segments = Array.isArray(transcript?.transcript_segments) ? transcript.transcript_segments : null;
  const hasSegments = segments && segments.length > 0;
  const hasContent = text.length > 0 || hasSegments;
  const showMissing = hasSegments && Array.isArray(segmentMissingInOther) && segmentMissingInOther.length === segments.length && missingLabel;
  const syncVideo = hasSegments && typeof videoCurrentTime === 'number' && typeof onSegmentSeek === 'function';

  const contentInner = (
    <>
      {hasSegments ? (
        <ul className="space-y-2 list-none p-0 m-0">
          {segments.map((seg, i) => {
            const isMissing = showMissing && segmentMissingInOther[i];
            const isActive = syncVideo && videoCurrentTime >= seg.start && videoCurrentTime <= seg.end;
            const isClickable = syncVideo;
            return (
              <li
                key={i}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? () => onSegmentSeek(seg.start) : undefined}
                onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSegmentSeek(seg.start); } } : undefined}
                className={`text-sm rounded px-2 py-1 -mx-2 -my-0.5 min-w-0 ${isMissing ? 'bg-amber-500/15 border-l-2 border-amber-500' : ''} ${isClickable ? 'cursor-pointer' : ''} ${isActive ? 'bg-primary/20' : ''}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground font-mono text-xs">
                    {formatSegmentTime(seg.start)} – {formatSegmentTime(seg.end)}
                  </span>
                  {isMissing && (
                    <span className="text-amber-700 dark:text-amber-400 text-xs font-medium" title={missingLabel}>
                      {missingLabel}
                    </span>
                  )}
                </div>
                <p className="text-foreground mt-0.5 whitespace-pre-wrap break-words leading-relaxed">
                  {seg.text || ''}
                </p>
              </li>
            );
          })}
        </ul>
      ) : hasContent ? (
        <p className="text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed">
          {text}
        </p>
      ) : (
        <p className="text-muted-foreground text-sm italic">No transcript available</p>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-1">
        {contentInner}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg flex flex-col min-h-[200px] shadow-subtle-sm">
      <div className="flex items-center justify-between p-2 border-b border-border flex-shrink-0">
        <h3 className="text-card-foreground text-sm font-semibold">{title}</h3>
        {transcript?.language_code && (
          <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs font-medium">
            {transcript.language_code.toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-1 bg-muted/30 rounded-b-lg p-3 border-t-0 border-border overflow-y-auto overflow-x-hidden max-h-[320px]">
        {contentInner}
      </div>
    </div>
  );
};

export default TranscriptionReadOnlyBox;
